// The ring of the tab "Maschine" while the machine does something, drawn as the round display draws it (Dominik
// 22.10.2026: "auf dem Display läuft der Ring von oben mitten komplett einmal durch … die Animationen in der App sollen
// dieselben sein wie auf dem Display"; firmware lib/RoundDisplay RoundDisplayUi.cpp drawBrew, drawBrewWeightRing,
// drawStopwatch, drawFlushSteps, drawBackflush, drawDescale, drawWaterTankEmpty): a whole circle from 12 o'clock,
// clockwise; a shot fills it with the brew colour from dark to light with a bright tip at its end; water fills it once
// in its time, or one segment per pulse, cycle or round. The temperature keeps the 270° gauge (components/Gauge.tsx),
// as the display's. Since 23.10.2026 only the direction and order follow the display; the look is the app's (Dominik:
// "der kreis irgendwie zu kantig … mir war nur wichtig, dass die richtung diesselbe ist"): round ends, no ticks, no
// running light.
import { liveOf, tempTone } from '../status.js';
import { readyBand } from './ready.js';
import type { ScreenContext } from './context.js';
import { formatAuto, formatNumber, signed } from './format.js';
import { LIVE_RUN_ON_S, liveEstimate, type LiveShotScreen } from './machine.js';

/** Colours of the display (RoundDisplayTheme.h): kBrew, kWater, kCool, kReady, kHeat, the dim text, kWeight. */
export type DialTone = 'brew' | 'water' | 'cool' | 'ready' | 'heat' | 'dim' | 'text' | 'weight';

export type DialRing =
  /** one arc from 12 o'clock: `fill` of the circle (0..1), `over` a second lap beyond it */
  | {
      type: 'arc';
      fill: number;
      over: number;
      tone: 'brew' | 'water';
      /** the lap beyond the target: the heat colour (by weight), a brighter brew (by time) */
      overTone: 'heat' | 'light';
      /**
       * where the tip sits, in circles from 12 o'clock and not wrapped: past 1 it is the end of the lap beyond the
       * target. After the shot it stays at the end, lying on the start of a closed ring.
       */
      marker: number;
    }
  /** segments with round ends and a gap between them: one per pulse (filling while it runs), per cycle or round (the
   * running one half bright) */
  | { type: 'segments'; tone: 'brew' | 'water' | 'cool'; parts: { fill: number; current: boolean }[] }
  /** the whole ring in a dim water colour (the tank is empty) */
  | { type: 'full'; tone: 'water' };

export interface DialView {
  kind: 'shot' | 'rinse' | 'flush' | 'backflush' | 'descale' | 'tank';
  label: string;
  labelTone: DialTone;
  /** the check before "FERTIG" */
  check: boolean;
  /** the big value; empty with an icon instead */
  value: string;
  unit: string | null;
  /** up right of the number: the pre-infusion that ran before the time shown ("+6") */
  sup: string | null;
  /** a second number under the big one: the time under the weight, the weight under the time */
  second: { text: string; sup: string | null; tone: DialTone } | null;
  /** the rows under it (target, deviation, temperature, what comes next) */
  rows: { text: string; tone: DialTone }[];
  icon: 'drop' | null;
  ring: DialRing;
  /** for the small ring over the tab bar: how full the ring is (0..1) */
  share: number;
  /** the seconds shown, for keeping a running ring from going back a frame */
  clock?: number;
}

/** seconds per revolution of the stopwatch without a target time (kStopwatchScale) */
export const STOPWATCH_S = 30;

const fmt1 = (ctx: ScreenContext, v: number) => formatNumber(v, 1, ctx.lang);

/** "+6", "+6,5": the pre-infusion as the display writes it up right of the number. */
const piSup = (ctx: ScreenContext, seconds: number) =>
  `+${formatNumber(seconds, Math.abs(seconds % 1) > 0.05 ? 1 : 0, ctx.lang)}`;

/** The temperature row of the display: green within the ready band, orange below, blue above. */
export function tempRow(ctx: ScreenContext): { text: string; tone: DialTone } | null {
  const live = liveOf(ctx.view, ctx.now);
  const temp = live?.currentTemp;
  if (typeof temp !== 'number') return null;
  const set = typeof live?.targetTemp === 'number' ? live.targetTemp : ctx.num('brew.setpoint');
  // drawTemperatureRow: green within the band and the hold, without the state of the ring
  return { text: `${fmt1(ctx, temp)}°`, tone: tempTone(temp, set, readyBand(ctx), true) };
}

/**
 * The running and the finished shot as the display draws it. `now` is this device's clock of the frame: time and
 * weight run on from the last value with the last flow, at most LIVE_RUN_ON_S (liveEstimate), so the ring and the
 * numbers move every frame as on the display.
 */
export function shotDial(ctx: ScreenContext, s: LiveShotScreen, now: number): DialView {
  const done = s.phase === 'done';
  const inPi = s.phase === 'pi';
  const est = liveEstimate(s, now);
  const extra =
    done || s.sampledAt === null ? 0 : Math.min(LIVE_RUN_ON_S, Math.max(0, (now - s.sampledAt) / 1000));
  const seconds = done ? s.seconds : est.seconds;
  const weight = s.weight === null ? null : done ? s.weight : s.weight + Math.max(0, s.flowRate ?? 0) * extra;
  const sup = s.preinfusion > 0 && !inPi ? piSup(ctx, s.preinfusion) : null;
  const label = {
    label: ctx.tr(done ? 'dialDone' : inPi ? 'dialPi' : 'dialBrew'),
    labelTone: (done ? 'ready' : 'brew') as DialTone,
    check: done,
  };
  if (s.goal?.unit === 'g' && weight !== null) {
    // by weight: the weight ends the shot, the ring shows it up to the target; beyond it in the heat colour
    const fill = Math.max(0, weight / s.goal.value);
    const dev = s.result?.deviation ?? signed(Math.round((weight - s.goal.value) * 10) / 10, ctx.lang) + ' g';
    return {
      kind: 'shot',
      ...label,
      value: fmt1(ctx, weight),
      unit: 'g',
      sup: null,
      second: { text: `${fmt1(ctx, seconds)} s`, sup, tone: 'dim' },
      rows: done
        ? [{ text: dev, tone: Math.abs(weight - s.goal.value) <= 1 ? 'ready' : 'heat' }]
        : [{ text: `${ctx.tr('goal')} ${formatAuto(s.goal.value, ctx.lang)} g`, tone: 'dim' }],
      icon: null,
      ring: {
        type: 'arc',
        fill: Math.min(fill, 1),
        over: Math.min(Math.max(fill - 1, 0), 1),
        tone: 'brew',
        overTone: 'heat',
        marker: Math.min(fill, 2),
      },
      share: Math.min(fill, 1),
      clock: seconds,
    };
  }
  // by time, by hand or by weight without the scale: the time, on the target (after the pre-infusion) or as a stopwatch
  const lap = s.goal?.unit === 's' ? s.goal.value : STOPWATCH_S;
  const fill = inPi ? 0 : Math.max(0, seconds / lap);
  const temp = tempRow(ctx);
  const goalRow =
    s.goal?.unit === 's'
      ? { text: `${ctx.tr('goal')} ${formatAuto(s.goal.value, ctx.lang)} s`, tone: 'dim' as const }
      : null;
  const scaleLost = s.scaleLost === true;
  const rows: { text: string; tone: DialTone }[] = scaleLost
    ? [{ text: ctx.tr('dialScaleLost'), tone: 'dim' }, ...(goalRow ? [goalRow] : [])]
    : weight !== null
      ? []
      : [...(goalRow ? [goalRow] : []), ...(temp ? [temp] : [])];
  return {
    kind: 'shot',
    ...label,
    value: fmt1(ctx, seconds),
    unit: 's',
    sup,
    second:
      weight !== null && !scaleLost ? { text: `${fmt1(ctx, weight)} g`, sup: null, tone: 'weight' } : null,
    rows,
    icon: null,
    ring: {
      type: 'arc',
      fill: Math.min(fill, 1),
      over: Math.min(Math.max(fill - 1, 0), 1),
      tone: 'brew',
      overTone: 'light',
      marker: Math.min(fill, 2), // not wrapped: the tip turns on past 12 (Dial.tsx)
    },
    share: Math.min(fill, 1),
    clock: seconds,
  };
}
